import axiosInstance from './axiosInstance';
import { BASE_URL } from '../settings';

const DASHBOARD_API_BASE = `${BASE_URL}md/dashboard`;

/**
 * Get dashboard analytics overview with all metrics
 * @param {string} startDate - Start date in format YYYY-MM-DD (optional)
 * @param {string} endDate - End date in format YYYY-MM-DD (optional)
 * @returns {Promise} Dashboard analytics data
 */
export const getDashboardAnalytics = (startDate, endDate) => {
  const params = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  return axiosInstance
    .get(`${DASHBOARD_API_BASE}/analytics`, { params })
    .then(res => res.data)
    .catch(error => {
      console.error('Error fetching dashboard analytics:', error);
      throw error;
    });
};

/**
 * Get recent bookings within the date range
 * @param {string} startDate - Start date in format YYYY-MM-DD (optional)
 * @param {string} endDate - End date in format YYYY-MM-DD (optional)
 * @param {number} page - Page number (default: 1)
 * @param {number} size - Page size (default: 10)
 * @returns {Promise} Page of recent bookings
 */
export const getRecentBookings = (startDate, endDate, page = 1, size = 10) => {
  const params = { page, size };
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  return axiosInstance
    .get(`${DASHBOARD_API_BASE}/recent-bookings`, { params })
    .then(res => res.data)
    .catch(error => {
      console.error('Error fetching recent bookings:', error);
      throw error;
    });
};

/**
 * Get top performing spaces by booking count and revenue
 * @param {string} startDate - Start date in format YYYY-MM-DD (optional)
 * @param {string} endDate - End date in format YYYY-MM-DD (optional)
 * @param {number} limit - Maximum number of top spaces to return (default: 10)
 * @returns {Promise} List of top performing spaces
 */
export const getTopSpaces = (startDate, endDate, limit = 10) => {
  const params = { limit };
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  return axiosInstance
    .get(`${DASHBOARD_API_BASE}/top-spaces`, { params })
    .then(res => res.data)
    .catch(error => {
      console.error('Error fetching top spaces:', error);
      throw error;
    });
};

/**
 * Get top users (frequent guests) by booking count
 * @param {string} startDate - Start date in format YYYY-MM-DD (optional)
 * @param {string} endDate - End date in format YYYY-MM-DD (optional)
 * @param {number} limit - Maximum number of top users to return (default: 10)
 * @returns {Promise} List of top users
 */
export const getTopUsers = (startDate, endDate, limit = 10) => {
  const params = { limit };
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  return axiosInstance
    .get(`${DASHBOARD_API_BASE}/top-users`, { params })
    .then(res => res.data)
    .catch(error => {
      console.error('Error fetching top users:', error);
      throw error;
    });
};

/**
 * Get booking count by status or type
 * @param {Object} params - Query params (status, type)
 * @returns {Promise} Count object
 */
export const getBookingCount = (params) => {
  return axiosInstance
    .get(`${DASHBOARD_API_BASE}/count`, { params })
    .then(res => res.data)
    .catch(error => {
      console.error('Error fetching booking count:', error);
      throw error;
    });
};

export const getActivePromotionBanner = () => {
  return axiosInstance
    .get(`${BASE_URL}ui/promotion-banners/active`)
    .then(res => res.data)
    .catch(error => {
      console.error('Error fetching active promotion banner:', error);
      return null;
    });
};

export const trackBannerClick = (bannerId) => {
  return axiosInstance
    .post(`${BASE_URL}ui/promotion-banners/${bannerId}/click`)
    .catch(error => {
      console.error('Error tracking banner click:', error);
    });
};
